/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useSelector } from 'react-redux';
import { Box, List, ListItem, styled } from '@mui/material';
import React, { useState, useEffect, useMemo } from 'react';
import { StoreInfo } from '../../../stores/storage/stores.types';
import TextField from '../../../../components/TextField';
import TestIds from '../../../../testIds';
import MaterialSymbol from '../../../../components/MaterialSymbol';
import { colors } from '@farmlands/mui-theme';
import { PadActionButton, PadLineItem } from '../../../../components/Pad/Pad';
import { getStoreHours, selectPickupStores } from '../../../stores/storage/stores.slice';
import { useAppDispatch } from '../../../../store/rootStore';
import { selectCurrentCart } from '../../../cart/storage/cart.slice';
import Typography from '../../../../components/Typography';
import { matchWeekdayHours } from '../../../cart/utils/matchStoreHours';
import { formatHours } from '../../../../util/formatAMPM';
import { StockAvailability } from './StockAvailability';
import { CartDeliveryMode } from '../../../cart/storage/cart.types';

export const StoreContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  height: '65vh',
  [theme.breakpoints.down('sm')]: {
    height: '100vh',
  },
}));

export const StoreBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const StoreBoxRow = styled(StoreBox)({
  flexDirection: 'row',
});

const StoreHoursBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '72px',
});

// False positives, Props is used as a PropType under useStoreSelector fn definition
interface Props {
  productId?: number;
  value: StoreInfo | undefined;
  setValue: React.Dispatch<React.SetStateAction<StoreInfo | undefined>>;
}

interface SelectedStoreButtonProps {
  value?: StoreInfo;
  stockProductId?: number;
  isEditable?: boolean;
  showHours?: boolean;
  onClickEdit?: () => void;
}
export const SelectedStoreButton = ({
  value,
  stockProductId,
  isEditable,
  onClickEdit,
  showHours = true,
}: SelectedStoreButtonProps) => {
  const renderSelectedStoreHours = React.useMemo(() => {
    if (!value?.storeHours) {
      return;
    }
    const matchWeekdays = matchWeekdayHours(value.storeHours);
    const mon = value.storeHours.find((x) => x.dayOfWeek === 'Monday');
    const tue = value.storeHours.find((x) => x.dayOfWeek === 'Tuesday');
    const wed = value.storeHours.find((x) => x.dayOfWeek === 'Wednesday');
    const thu = value.storeHours.find((x) => x.dayOfWeek === 'Thursday');
    const fri = value.storeHours.find((x) => x.dayOfWeek === 'Friday');
    const sat = value.storeHours.find((x) => x.dayOfWeek === 'Saturday');
    const sun = value.storeHours.find((x) => x.dayOfWeek === 'Sunday');

    return (
      <div data-testid={TestIds.feat.pickUpStore.selectedOpenHours}>
        {matchWeekdays && <Typography variant='body2'>Mon-Fri: {formatHours(mon)}</Typography>}
        {!matchWeekdays && (
          <>
            <Typography variant='body2'>Mon: {formatHours(mon)}</Typography>
            <Typography variant='body2'>Tue: {formatHours(tue)}</Typography>
            <Typography variant='body2'>Wed: {formatHours(wed)}</Typography>
            <Typography variant='body2'>Thu: {formatHours(thu)}</Typography>
            <Typography variant='body2'>Fri: {formatHours(fri)}</Typography>
          </>
        )}
        <Typography variant='body2'>Sat: {formatHours(sat)}</Typography>
        <Typography variant='body2'>Sun: {formatHours(sun)}</Typography>
      </div>
    );
  }, [value]);

  return (
    <PadActionButton
      onClick={() => {
        onClickEdit?.();
      }}
      buttonTestId={TestIds.components.address.editButton}
      title='Pick up from:'
      subtitle={
        <>
          <StoreBoxRow>
            <Typography
              variant='body1'
              color={'primary500'}
              data-testid={TestIds.feat.pickUpStore.selectedStoreBranchName}
            >
              {value?.orgUnitName}
            </Typography>
          </StoreBoxRow>
          <StoreBox>
            <Typography variant='body2' data-testid={TestIds.feat.pickUpStore.selectedStoreAddress}>
              {value?.address}
            </Typography>
            {showHours && <StoreHoursBox>{renderSelectedStoreHours}</StoreHoursBox>}
          </StoreBox>
          <StockAvailability productId={stockProductId ?? 0} storeId={value?.inventoryLocationId} />
        </>
      }
      isEditEnabled={isEditable}
    />
  );
};

const StoreSelector = (props: Props) => {
  const { Components } = useStoreSelector(props);

  return (
    <StoreContainer>
      {Components.selectedStore}
      {Components.searchBar}
      {Components.filteredList}
    </StoreContainer>
  );
};

export const useStoreSelector = ({ productId, value, setValue }: Props) => {
  const dispatch = useAppDispatch();

  const stores = useSelector(selectPickupStores);
  const cart = useSelector(selectCurrentCart);

  const [searchText, setSearchText] = useState<string>('');
  const [filteredStores, setFilteredStores] = useState<StoreInfo[]>(stores);

  useEffect(() => {
    if (cart?.cartDeliveryMode === CartDeliveryMode.PICKUP && cart?.pickupStore) {
      dispatch(getStoreHours(cart?.pickupStore?.inventorySiteId));
      setValue(
        stores.find((x) => x.inventoryLocationId === cart?.pickupStore?.inventoryLocationId),
      );
    }
  }, [cart?.pickupStore]);

  useEffect(() => {
    if (value) {
      setValue(stores.find((x) => x.inventoryLocationId === value?.inventoryLocationId));

      const filtered = stores.filter(
        (x) =>
          x.address.toLowerCase().includes(searchText.toLowerCase()) ||
          x.orgUnitName.toLowerCase().includes(searchText.toLowerCase()),
      );
      setFilteredStores(filtered);
    } else if (stores.length > 0 && filteredStores.length === 0 && searchText === '') {
      setFilteredStores(stores);
    }
  }, [stores, value, searchText]);

  useEffect(() => {
    if (!searchText && searchText !== '') {
      setFilteredStores(stores);
    }
  }, [stores]);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = event.target.value;
    setSearchText(inputValue);

    const filtered = stores.filter(
      (x) =>
        x.address.toLowerCase().includes(inputValue.toLowerCase()) ||
        x.orgUnitName.toLowerCase().includes(inputValue.toLowerCase()),
    );
    setFilteredStores(filtered);
  };

  const handleOnClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, value: StoreInfo) => {
    if (value && typeof value === 'object' && value.orgUnitName && value.inventoryLocationId) {
      const store = stores.find((x) => x.inventoryLocationId === value.inventoryLocationId);
      if (store) {
        dispatch(getStoreHours(value.inventorySiteId));
        setValue(store);

        const modalDialogContent = document.getElementsByClassName('MuiDialogContent-root')[0];
        if (modalDialogContent) modalDialogContent.scrollTo({ top: 0, behavior: 'auto' });
      }
    } else {
      setValue(undefined);
    }
  };

  const renderSelectedPickUpStore = useMemo(() => {
    return <SelectedStoreButton value={value} stockProductId={productId} />;
  }, [value?.orgUnitName, value?.address, value?.storeHours, productId]);

  const filteredList = (
    <List sx={{ p: 0, gap: 0 }}>
      {filteredStores.map((store) => (
        <ListItem key={store.channelId} sx={{ m: 0, p: 0 }}>
          <PadLineItem
            sx={{ justifyContent: 'space-between' }}
            data-testid={TestIds.feat.pickUpStore.storeListItem}
            data-id={store.orgUnitName}
            key={store.inventorySiteId}
            option={{
              title: store.orgUnitName,
              subtitle: `${store.street}, ${store.city}`,
              sideElement: (
                <StockAvailability productId={productId ?? 0} storeId={store.inventoryLocationId} />
              ),
            }}
            onClick={(event) => handleOnClick(event, store)}
          />
        </ListItem>
      ))}
    </List>
  );

  const searchBar = (
    <TextField
      label={'Search stores:'}
      value={searchText}
      placeholder='Search Farmlands stores'
      testId={TestIds.feat.pickUpStore.searchStoreField}
      variant={'outlined'}
      onChange={handleOnChange}
      autoFocus={true}
      inputProps={{
        maxLength: 150,
      }}
      InputProps={{
        endAdornment: <MaterialSymbol sx={{ color: colors.primary500 }}>search</MaterialSymbol>,
      }}
    />
  );

  const selectedStore = value?.orgUnitName && renderSelectedPickUpStore;

  return {
    Components: {
      Container: StoreContainer,
      filteredList,
      searchBar,
      selectedStore,
    },
    values: {
      searchText,
      filteredStores,
    },
  };
};

export default StoreSelector;
